import "./styles.css";

type LiveEvent = {
  id: string;
  title: string;
  date_label: string;
  location_label: string;
  community_url: string;
};

type Session = {
  id: string;
  label: string;
  title: string;
  speakers: string[];
  role: string | null;
  position: number;
};

type Question = {
  id: string;
  event_id: string;
  session_id: string;
  body: string;
  author_name: string | null;
  is_anonymous: boolean;
  status: "new" | "review" | "answered" | "hidden";
  pinned: boolean;
  created_at: string;
  session_label: string;
  session_title: string;
  session_speakers: string[];
  session_role?: string | null;
  score: number;
  user_vote?: number;
};

type AppState = {
  mode: "public" | "admin";
  theme: "light" | "dark";
  event: LiveEvent | null;
  sessions: Session[];
  questions: Question[];
  selectedSessionId: string;
  composerOpen: boolean;
  statusFilter: "open" | "answered" | "all";
  loading: boolean;
  error: string | null;
  adminToken: string;
};

const appElement = document.querySelector<HTMLDivElement>("#app");

if (!appElement) {
  throw new Error("Missing app root");
}

const app = appElement;

const state: AppState = {
  mode: window.location.pathname.startsWith("/admin") ? "admin" : "public",
  theme: getInitialTheme(),
  event: null,
  sessions: [],
  questions: [],
  selectedSessionId: "all",
  composerOpen: false,
  statusFilter: "open",
  loading: true,
  error: null,
  adminToken: sessionStorage.getItem("iadevs-live-admin-token") ?? "",
};

const voterId = getOrCreateVoterId();
applyTheme();

void boot();

async function boot(): Promise<void> {
  bindEvents();
  await loadEvent();
}

function bindEvents(): void {
  app.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;
    const actionButton = target.closest<HTMLElement>("[data-action]");
    if (!actionButton) return;
    if (actionButton.classList.contains("modal-backdrop") && event.target !== actionButton) return;

    const action = actionButton.dataset.action;

    if (action === "open-composer") {
      state.composerOpen = true;
      render();
      return;
    }

    if (action === "toggle-theme") {
      state.theme = state.theme === "dark" ? "light" : "dark";
      localStorage.setItem("iadevs-live-theme", state.theme);
      applyTheme();
      render();
      return;
    }

    if (action === "close-composer") {
      state.composerOpen = false;
      render();
      return;
    }

    if (action === "select-session") {
      state.selectedSessionId = actionButton.dataset.sessionId ?? "all";
      void loadQuestions();
      return;
    }

    if (action === "filter-status") {
      state.statusFilter = (actionButton.dataset.status ?? "open") as AppState["statusFilter"];
      void loadQuestions();
      return;
    }

    if (action === "vote") {
      const questionId = actionButton.dataset.questionId;
      const value = Number(actionButton.dataset.value ?? 0);
      if (questionId) void vote(questionId, value);
      return;
    }

    if (action === "admin-update") {
      const questionId = actionButton.dataset.questionId;
      const status = actionButton.dataset.status;
      const pinned = actionButton.dataset.pinned === "true";
      if (questionId && status) void updateAdminQuestion(questionId, status, pinned);
    }
  });

  app.addEventListener("submit", (event) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;

    if (form.id === "question-form") {
      void submitQuestion(form);
      return;
    }

    if (form.id === "admin-token-form") {
      const token = String(new FormData(form).get("token") ?? "").trim();
      state.adminToken = token;
      sessionStorage.setItem("iadevs-live-admin-token", token);
      void loadQuestions();
    }
  });
}

async function loadEvent(): Promise<void> {
  state.loading = true;
  state.error = null;
  render();

  try {
    const data = await api<{ event: LiveEvent; sessions: Session[] }>("/api/event");
    state.event = data.event;
    state.sessions = data.sessions;
    await loadQuestions();
  } catch (error) {
    state.error = errorMessage(error);
    state.loading = false;
    render();
  }
}

async function loadQuestions(): Promise<void> {
  if (!state.event) return;
  state.loading = true;
  state.error = null;
  render();

  try {
    const sessionParam = state.selectedSessionId === "all" ? "" : `&sessionId=${state.selectedSessionId}`;
    const endpoint =
      state.mode === "admin"
        ? `/api/admin/questions?eventId=${state.event.id}`
        : `/api/questions?eventId=${state.event.id}&voterId=${voterId}&status=${state.statusFilter}${sessionParam}`;

    const data = await api<{ questions: Question[] }>(endpoint, {
      headers: state.mode === "admin" && state.adminToken ? { Authorization: `Bearer ${state.adminToken}` } : {},
    });
    state.questions = data.questions;
  } catch (error) {
    state.error = errorMessage(error);
  } finally {
    state.loading = false;
    render();
  }
}

async function submitQuestion(form: HTMLFormElement): Promise<void> {
  if (!state.event) return;

  const data = new FormData(form);
  const sessionId = String(data.get("sessionId") ?? "");
  const body = String(data.get("body") ?? "").trim();
  const authorName = String(data.get("authorName") ?? "").trim();
  const isAnonymous = authorName.length === 0;

  try {
    await api("/api/questions", {
      method: "POST",
      body: JSON.stringify({
        eventId: state.event.id,
        sessionId,
        body,
        authorName,
        isAnonymous,
      }),
    });
    state.composerOpen = false;
    await loadQuestions();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function vote(questionId: string, requestedValue: number): Promise<void> {
  const current = state.questions.find((question) => question.id === questionId)?.user_vote ?? 0;
  const value = current === requestedValue ? 0 : requestedValue;

  try {
    await api("/api/votes", {
      method: "POST",
      body: JSON.stringify({ questionId, voterId, value }),
    });
    await loadQuestions();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

async function updateAdminQuestion(questionId: string, status: string, pinned: boolean): Promise<void> {
  try {
    await api("/api/admin/questions", {
      method: "PATCH",
      headers: state.adminToken ? { Authorization: `Bearer ${state.adminToken}` } : {},
      body: JSON.stringify({ questionId, status, pinned }),
    });
    await loadQuestions();
  } catch (error) {
    state.error = errorMessage(error);
    render();
  }
}

function render(): void {
  document.body.classList.toggle("modal-open", state.composerOpen);
  app.innerHTML = state.mode === "admin" ? renderAdmin() : renderPublic();
}

function renderPublic(): string {
  return `
    <main class="shell">
      ${renderHeader()}
      ${renderStatus()}
      <section class="hero-panel">
        <h1 class="sr-only">Preguntas</h1>
        <p>Elige la charla, escribe una pregunta y vota las que quieres escuchar en vivo.</p>
        <button class="primary-button" type="button" data-action="open-composer">
          ${icon("plus")}
          <span>Hacer una pregunta</span>
        </button>
      </section>
      ${state.composerOpen ? renderComposer() : ""}
      ${renderSessionRail()}
      ${renderFilters()}
      ${renderQuestionList()}
      ${renderCommunityBanner()}
    </main>
  `;
}

function renderAdmin(): string {
  if (!state.event) {
    return `
      <main class="shell admin-shell">
        ${renderHeader()}
        ${renderStatus()}
      </main>
    `;
  }

  return `
    <main class="shell admin-shell">
      ${renderHeader()}
      <section class="admin-panel">
        <h1>Moderacion</h1>
        <p>Vista oculta para ordenar preguntas, fijarlas y marcarlas como respondidas.</p>
        <form id="admin-token-form" class="admin-token">
          <label>
            Token admin
            <input name="token" type="password" value="${escapeHtml(state.adminToken)}" autocomplete="current-password" />
          </label>
          <button type="submit">Entrar</button>
        </form>
      </section>
      ${renderStatus()}
      <section class="admin-list">
        ${state.questions.map(renderAdminQuestion).join("") || renderEmpty("Todavia no hay preguntas.")}
      </section>
    </main>
  `;
}

function renderHeader(): string {
  const event = state.event;
  return `
    <header class="topbar">
      <div class="brand-lockup">
        <img class="brand" src="/iadevs-logo.png" alt="IADevs" />
      </div>
      <div class="event-meta">
        <strong>${escapeHtml(event?.title ?? "IADevs Live")}</strong>
        <span>${escapeHtml([event?.date_label, event?.location_label].filter(Boolean).join(" · "))}</span>
      </div>
      <button class="theme-button" type="button" aria-label="Cambiar tema" data-action="toggle-theme">
        ${icon(state.theme === "dark" ? "sun" : "moon")}
      </button>
    </header>
  `;
}

function renderStatus(): string {
  if (state.error) {
    return `<div class="notice error">${escapeHtml(state.error)}</div>`;
  }
  if (state.loading) {
    return `<div class="notice">Cargando preguntas...</div>`;
  }
  return "";
}

function renderComposer(): string {
  const activeSession = state.selectedSessionId === "all" ? state.sessions[0] : findSession(state.selectedSessionId);

  return `
    <div class="modal-backdrop" data-action="close-composer">
      <section class="composer-sheet" role="dialog" aria-modal="true" aria-labelledby="question-dialog-title">
        <div class="sheet-handle" aria-hidden="true"></div>
        <div class="composer-head">
          <h2 id="question-dialog-title">Nueva pregunta</h2>
          <button class="icon-button" type="button" aria-label="Cerrar" data-action="close-composer">${icon("x")}</button>
        </div>
        <form id="question-form">
          <fieldset class="session-picker">
            <legend>Charla</legend>
            ${state.sessions.map((session) => renderSessionRadio(session, activeSession?.id)).join("")}
          </fieldset>
          <label class="field">
            Tu pregunta
            <textarea name="body" minlength="8" maxlength="280" rows="4" required placeholder="¿Qué te gustaría preguntarle al speaker?"></textarea>
          </label>
          <label class="field compact-field author-field">
            Nombre (opcional)
            <input name="authorName" maxlength="80" placeholder="Tu nombre" />
          </label>
          <button class="primary-button wide" type="submit">
            ${icon("send")}
            <span>Enviar pregunta</span>
          </button>
        </form>
      </section>
    </div>
  `;
}

function renderSessionRail(): string {
  if (!state.sessions.length) return "";

  return `
    <section class="session-rail" aria-label="Charlas">
      <button class="talk-card all ${state.selectedSessionId === "all" ? "selected" : ""}" type="button" data-action="select-session" data-session-id="all">
        <strong>Todas</strong>
        <small>${state.sessions.length} charlas</small>
      </button>
      ${state.sessions.map(renderSessionCard).join("")}
    </section>
  `;
}

function renderSessionCard(session: Session): string {
  const selected = state.selectedSessionId === session.id ? "selected" : "";
  return `
    <button class="talk-card ${selected}" type="button" data-action="select-session" data-session-id="${escapeHtml(session.id)}">
      <strong>${escapeHtml(session.title)}</strong>
      <small>${escapeHtml(formatSpeakers(session.speakers))}</small>
    </button>
  `;
}

function renderSessionRadio(session: Session, activeId?: string): string {
  return `
    <label class="radio-card">
      <input type="radio" name="sessionId" value="${escapeHtml(session.id)}" ${session.id === activeId ? "checked" : ""} required />
      <span>
        <strong>${escapeHtml(session.title)}</strong>
        <small>${escapeHtml(formatSpeakers(session.speakers))}${session.role ? ` · ${escapeHtml(session.role)}` : ""}</small>
      </span>
    </label>
  `;
}

function renderFilters(): string {
  const filters: Array<[AppState["statusFilter"], string]> = [
    ["open", "Por responder"],
    ["answered", "Respondidas"],
    ["all", "Todas"],
  ];

  return `
    <nav class="filters" aria-label="Filtros de preguntas">
      ${filters
        .map(
          ([value, label]) => `
            <button type="button" data-action="filter-status" data-status="${value}" class="${state.statusFilter === value ? "active" : ""}">
              ${escapeHtml(label)}
            </button>
          `,
        )
        .join("")}
    </nav>
  `;
}

function renderQuestionList(): string {
  const questions = state.questions;
  return `
    <section class="question-list" aria-label="Preguntas">
      ${questions.map(renderQuestion).join("") || renderEmpty("Se la primera persona en preguntar.")}
    </section>
  `;
}

function renderQuestion(question: Question): string {
  const upActive = question.user_vote === 1 ? "active" : "";
  const downActive = question.user_vote === -1 ? "active" : "";
  return `
    <article class="question-card">
      <div class="vote-stack" aria-label="Votos">
        <button class="${upActive}" type="button" aria-label="Votar positivo" data-action="vote" data-question-id="${question.id}" data-value="1">${icon("chevron-up")}</button>
        <strong>${question.score}</strong>
        <button class="${downActive}" type="button" aria-label="Votar negativo" data-action="vote" data-question-id="${question.id}" data-value="-1">${icon("chevron-down")}</button>
      </div>
      <div class="question-body">
        <div class="question-meta">
          <span>${escapeHtml(question.session_title)}</span>
          ${question.status === "answered" ? "<mark>Respondida</mark>" : ""}
        </div>
        <p>${escapeHtml(question.body)}</p>
        <footer>
          <span>${escapeHtml(question.is_anonymous || !question.author_name ? "Anónima" : question.author_name)}</span>
          <time>${formatTime(question.created_at)}</time>
        </footer>
      </div>
    </article>
  `;
}

function renderAdminQuestion(question: Question): string {
  return `
    <article class="admin-question">
      <div>
        <span>${escapeHtml(question.session_title)}</span>
        <p>${escapeHtml(question.body)}</p>
        <small>${question.score} votos · ${escapeHtml(question.status)}${question.pinned ? " · fijada" : ""}</small>
      </div>
      <div class="admin-actions">
        <button type="button" data-action="admin-update" data-question-id="${question.id}" data-status="${question.status}" data-pinned="${!question.pinned}">
          ${question.pinned ? "Soltar" : "Fijar"}
        </button>
        <button type="button" data-action="admin-update" data-question-id="${question.id}" data-status="answered" data-pinned="${question.pinned}">Respondida</button>
        <button type="button" data-action="admin-update" data-question-id="${question.id}" data-status="hidden" data-pinned="false">Ocultar</button>
      </div>
    </article>
  `;
}

function renderCommunityBanner(): string {
  const url = state.event?.community_url ?? "https://www.iadevs.cl/comunidad";
  return `
    <aside class="community-banner">
      <span>Sumate a la comunidad IADevs</span>
      <a href="${escapeHtml(url)}" target="_blank" rel="noreferrer">iadevs.cl/comunidad</a>
    </aside>
  `;
}

function renderEmpty(message: string): string {
  return `
    <div class="empty-state">
      ${icon("message")}
      <p>${escapeHtml(message)}</p>
    </div>
  `;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(path, { ...init, headers });
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    throw new Error(typeof data.error === "string" ? data.error : "No se pudo completar la accion.");
  }

  return data as T;
}

function findSession(id: string): Session | undefined {
  return state.sessions.find((session) => session.id === id);
}

function getOrCreateVoterId(): string {
  const key = "iadevs-live-voter-id";
  const existing = localStorage.getItem(key);
  if (existing) return existing;

  const id = crypto.randomUUID();
  localStorage.setItem(key, id);
  return id;
}

function getInitialTheme(): AppState["theme"] {
  const stored = localStorage.getItem("iadevs-live-theme");
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(): void {
  document.documentElement.dataset.theme = state.theme;
}

function formatSpeakers(speakers: string[]): string {
  if (!speakers.length) return "";
  if (speakers.length === 1) return speakers[0];
  return `${speakers.slice(0, -1).join(", ")} y ${speakers.at(-1)}`;
}

function formatTime(value: string): string {
  return new Intl.DateTimeFormat("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Algo fallo.";
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function icon(
  name: "plus" | "send" | "x" | "chevron-up" | "chevron-down" | "message" | "moon" | "sun",
): string {
  const icons = {
    plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>',
    send: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m4 12 16-8-5 16-3-7-8-1Z"/><path d="m12 13 8-9"/></svg>',
    x: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    "chevron-up": '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 15 5-5 5 5"/></svg>',
    "chevron-down": '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 9 5 5 5-5"/></svg>',
    message: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v8Z"/></svg>',
    moon: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3a6.5 6.5 0 0 0 8.7 8.7A8.5 8.5 0 1 1 12 3Z"/></svg>',
    sun: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/></svg>',
  };

  return icons[name];
}
